import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  Box,
  Card,
  CardMedia,
  Typography,
  Button,
  IconButton,
  Skeleton,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useNavigate } from "react-router-dom";

const PagePosts = ({ token, pageId, platform = "instagram" }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showLeftChevron, setShowLeftChevron] = useState(false);
  const [showRightChevron, setShowRightChevron] = useState(true);
  const scrollContainerRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (pageId) {
      fetchPagePosts(pageId, platform);
    }
  }, [pageId, platform]);

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const fetchPagePosts = async (pageId, platform) => {
    setLoading(true);
    setPosts([]); // Clear previous posts to avoid stale data
    try {
      const endpoint = platform === "facebook" ? "facebook" : "instagram";
      const postsResponse = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/${endpoint}/posts/${pageId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (postsResponse.status === 200) {
        setPosts(postsResponse.data); // Store all posts in the state
      }
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleScroll = () => {
    const container = scrollContainerRef.current;
    if (container) {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      setShowLeftChevron(scrollLeft > 0);
      setShowRightChevron(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const scrollLeft = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: -400, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    const container = scrollContainerRef.current;
    if (container) {
      container.scrollBy({ left: 400, behavior: "smooth" });
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      handleScroll(); // Check initial state
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [posts]);

  const handleConfigure = (postId) => {
    const qs = new URLSearchParams({ postId }).toString();
    navigate(`/dashboard/comments/configure?${qs}`);
  };

  return (
    <Box sx={{ position: "relative" }}>
      {/* Left Chevron */}
      {showLeftChevron && (
        <IconButton
          onClick={scrollLeft}
          sx={{
            position: "absolute",
            left: -20,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            backgroundColor: "background.paper",
            boxShadow: 2,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>
      )}

      {/* Scrollable Container */}
      <Box
        ref={scrollContainerRef}
        sx={{
          display: "flex",
          gap: 2,
          overflowX: "auto",
          overflowY: "hidden",
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          pl: showLeftChevron ? 4 : 0,
          pr: showRightChevron ? 4 : 0,
        }}
      >
        {loading ? (
          Array.from(new Array(5)).map((_, index) => (
            <Card
              key={index}
              sx={{
                flexShrink: 0,
                width: "200px",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <Skeleton variant="rectangular" height={300} animation="wave" />
              <Box sx={{ position: "absolute", bottom: 0, left: 0, right: 0, p: 2 }}>
                <Skeleton variant="text" width="90%" sx={{ bgcolor: "grey.700" }} />
                <Skeleton variant="text" width="60%" sx={{ bgcolor: "grey.700" }} />
              </Box>
            </Card>
          ))
        ) : (
          posts.map((post) => (
          <Card
            key={post.id}
            sx={{
              flexShrink: 0,
              width: "200px",
              position: "relative",
              overflow: "hidden",
              borderRadius: 2,
            }}
          >
            <CardMedia
              component="img"
              sx={{
                width: "100%",
                height: "300px",
                objectFit: "cover",
              }}
              image={
                post.media_type === "VIDEO"
                  ? post.thumbnail_url
                  : post.media_url || "/placeholder-image.png"
              }
              alt={post.description || "Post image"}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "/placeholder-image.png";
              }}
            />
            {/* Overlay with gradient for better text readability */}
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                background: "linear-gradient(to top, rgba(0,0,0,0.8) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)",
                p: 2,
                pt: 3,
              }}
            >
              {/* Caption */}
              <Typography
                variant="body2"
                sx={{
                  color: "white",
                  mb: 1,
                  overflow: "hidden",
                  display: "-webkit-box",
                  WebkitBoxOrient: "vertical",
                  WebkitLineClamp: 2,
                  textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                }}
              >
                {post.caption || "No caption"}
              </Typography>
              
              {/* Timestamp */}
              <Typography
                variant="caption"
                sx={{
                  color: "rgba(255,255,255,0.9)",
                  display: "block",
                  mb: 1.5,
                  textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                }}
              >
                {formatTimestamp(post.timestamp)}
              </Typography>

              {/* Buttons */}
              <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => handleConfigure(post.id)}
                  sx={{
                    flex: 1,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  Configure
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    flex: 1,
                    color: "white",
                    borderColor: "rgba(255,255,255,0.5)",
                    textTransform: "none",
                    "&:hover": {
                      borderColor: "white",
                      backgroundColor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  Skip
                </Button>
              </Box>
            </Box>
          </Card>
          ))
        )}
      </Box>

      {/* Right Chevron */}
      {showRightChevron && (
        <IconButton
          onClick={scrollRight}
          sx={{
            position: "absolute",
            right: -20,
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            backgroundColor: "background.paper",
            boxShadow: 2,
            "&:hover": {
              backgroundColor: "action.hover",
            },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      )}
    </Box>
  );
};

export default PagePosts;
